/* global window */

import React, { useCallback, useEffect, useState, useRef, useContext, forwardRef, useImperativeHandle } from 'react';

import '../styles/SearchBox.css';
import AiOutlineSearch from '../icons/AiOutlineSearch';
import SearchResultsGroup from './SearchResultsGroup';
import SearchResultsItemCollection from './SearchResultsItemCollection';
import SearchResultsItemProduct from './SearchResultsItemProduct';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useIsRendered from '../hooks/useIsRendered';
import { LoadingContext } from './context/LoadingContext';
import getPricesFromQuery from '../helpers/getPricesFromQuery';
import Stack from 'react-bootstrap/Stack';
import PriceFilterSuggestion from './PriceFilterSuggestion';

const DEFAULT_NON_PAGE_LIMIT = 4;
const TIMEOUT_DURATION = 600;

// eslint-disable-next-line
function SearchBox({ isMobile, isPage = false, productList = [], setProductList = () => { }, searchLoaded = false, setSearchLoaded = () => { }, searchLoading = false, setSearchLoading = () => { }, searchQuery = {}, setSearchQuery = () => { }, setPagesData = () => { } }, searchBoxRef) {
    const isRendered = useIsRendered();
    const navigate = useNavigate();
    const searchBox = useRef(null);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const [collectionList, setCollectionList] = useState([]);
    const [prices, setPrices] = useState({
        minPrice: null,
        maxPrice: null,
    });
    const [searchParams, setParams] = useSearchParams();
    const [alterPath, setAlterPath] = useState(false);

    useImperativeHandle(searchBoxRef, () => ({
        enableAlterPath() {
            setAlterPath(true);
        }
    }), []);

    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    const handleSearch = useCallback(async (isFirstRender = false) => {
        try {
            if (searchQuery.q?.length < 3
                && searchQuery.v?.length < 3) 
                return;
            setSearchLoading(true);
            startLoading();
            const params = new URLSearchParams(Object.assign({}, ...Object.entries(searchQuery).filter(([_k, v]) => !Array.isArray(v) ? Boolean(v) : v.length > 0).map(([k, v]) => ({ [k]: v })), {
                ...!isPage
                === true && ({ limit: DEFAULT_NON_PAGE_LIMIT })
            })).toString();
            // Fetch data
            const [collectionResponse, productResponse] = await Promise.all([
                fetch(new URL('api/collections?' + params, process.env.APP_URL)),
                fetch(new URL('api/products/filter?' + params, process.env.APP_URL)),
            ]);
            // Collections
            const collectionData = await collectionResponse.json();
            setCollectionList(collectionData.collections ?? []);
            // Products
            const productData = await productResponse.json();
            setProductList(productData.products ?? []);
            if (productData.pageInfo) {
                setPagesData({
                    limit: productData.pageInfo.limitPerPage,
                    count: productData.pageInfo.totalObjects,
                    pages: productData.pageInfo.totalPages,
                    availableTagPrefixes: productData.pageInfo.availableTagPrefixes ?? [],
                    availableTagFilters: productData.pageInfo.availableTagFilters ?? [],
                    availableVendors: productData.pageInfo.availableVendors ?? [],
                    productMaxPrice: productData.pageInfo.productMaxPrice,
                });
            }
            // Update Search
            setSearchLoaded(true);
            setSearchLoading(false);
            finishLoading();
            if (!isFirstRender && !isPage) {
                setSearchExpanded(true);
            }
        } catch (e) {
            console.error(e);
            setSearchLoading(false);
            setSearchLoaded(false);
            finishLoading();
        }
    }, [searchQuery, isPage]);
    useEffect(() => {
        let autoSearchTimer = null;
        const AUTO_SEARCH_WAIT_MS = isPage ? 400 : 600;
        autoSearchTimer = setTimeout(() => handleSearch(!isRendered), AUTO_SEARCH_WAIT_MS);
        if (!searchQuery.q) {
            setSearchExpanded(false);
        }
        return () => {
            clearTimeout(autoSearchTimer);
        };
    }, [handleSearch]);
    useEffect(() => {
        // Detect outside clicks
        const detectOutsideClick = (e) => {
            if (searchBox.current
                && !searchBox.current.contains(e.target)
                && searchExpanded === true) {
                setSearchExpanded(false);
            }
        };
        window.addEventListener('click', detectOutsideClick, true);
        return () => {
            window.removeEventListener('click', detectOutsideClick, true);
        };
    }, [searchExpanded]);
    const handleSearchButtonPress = () => {
        if (isPage) 
            return handleSearch();
        if (!searchLoaded) {
            if (searchQuery.q?.length > 0
                || searchQuery.v?.length > 0) 
                return handleSearch();
            return;
        }
        setSearchExpanded(!searchExpanded);
    };
    const genResultsUrl = () => (`/pages/search-results?${new URLSearchParams(Object.assign({}, ...Object.entries(searchQuery).filter(([_k, v]) => !Array.isArray(v) ? Boolean(v) : v.length > 0).map(([k, v]) => ({ [k]: v })), { page: 0 })).toString()}`);
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (!isPage) 
                return navigate(genResultsUrl());
            return handleSearch();
        }
    };

    const setPricesWrapper = updates => {
        setPrices(prevPrices => ({ 
            ...prevPrices,
            ...updates, 
        }));
    };

    const onQueryChange = e => {
        setSearchQuery({
            ...searchQuery,
            q: e.target.value,
        });
    };

    const onPriceSuggestionAccept = (price) => {
        let priceObject = prices[price];

        if (!priceObject) {
            priceObject = {};
        }
        
        setSearchQuery({
            ...searchQuery,
            q: prices[price].newQuery,
            [price]: priceObject.value,
        });

        setPricesWrapper({ [price]: null });

        if (alterPath) {
            const currentParams = Object.fromEntries(searchParams.entries());
            const updatedParams = {
                ...currentParams,
                [price]: priceObject.value
            };
        
            setParams(updatedParams);
        }
    };

    // Set minPrice, maxPrice from search query
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const { minPrice: oldMinPrice, maxPrice: oldMaxPrice } = searchQuery;
            const { minPrice, maxPrice } = getPricesFromQuery(searchQuery.q);
              
            if (minPrice && oldMaxPrice && parseFloat(oldMaxPrice) > parseFloat(minPrice.value)) {
                setPricesWrapper({ minPrice });
            } else {
                setPricesWrapper({ minPrice: null });
            }
            
            if (maxPrice && oldMinPrice && parseFloat(oldMinPrice) < parseFloat(maxPrice.value)) {
                setPricesWrapper({ maxPrice });
            } else {
                setPricesWrapper({ maxPrice: null });
            }
            
            if (!oldMaxPrice && minPrice) {
                setPricesWrapper({ minPrice });
            }
            
            if (!oldMinPrice && maxPrice) {
                setPricesWrapper({ maxPrice });
            }
        }, TIMEOUT_DURATION);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const { minPrice, maxPrice } = prices;

    return (<div
        ref={searchBox}
        className={'search-box'}>
        <input
            type={'text'}
            placeholder={'What are you looking for?'}
            value={searchQuery.q}
            onChange={onQueryChange}
            onFocus={() => setSearchExpanded(true)}
            onKeyDown={handleSearchKeyPress} />
        <button
            type={'submit'}
            aria-label={'Search'}
            onClick={handleSearchButtonPress}>
            <AiOutlineSearch
                size={'25px'} />
        </button>
        {(searchLoaded && !isPage) && (<div
            className={'search--results'}
            style={{ ...searchExpanded === true ? ({ display: 'flex' }) : ({ display: 'none' }) }}>
            <Stack
                direction={'horizontal'} 
                className={'py-3 px-2'}
                gap={1}
            >
                {minPrice ? (
                    <PriceFilterSuggestion
                        onClick={() => onPriceSuggestionAccept('minPrice')}
                        matchedString={minPrice.matchedString}
                    />
                ): null}
                {maxPrice ? (
                    <PriceFilterSuggestion
                        onClick={() => onPriceSuggestionAccept('maxPrice')}
                        matchedString={maxPrice.matchedString}
                    />
                ): null}
            </Stack>
            {!collectionList.length && !productList.length && (<p
                style={{ padding: '5px 10px' }}>No results found.</p>)}
            {collectionList.length > 0 && (<SearchResultsGroup
                title={'Categories'}>
                {collectionList.map((collection) => (<SearchResultsItemCollection
                    onClick={() => setSearchExpanded(false)}
                    key={collection.id}
                    title={collection.title}
                    query={searchQuery.q}
                    minPrice={searchQuery.minPrice}
                    maxPrice={searchQuery.maxPrice}
                    handle={collection.handle} />))}
            </SearchResultsGroup>)}
            {productList.length > 0 && (<SearchResultsGroup
                title={'Products'}>
                {productList
                    .sort((a, b) => {
                        if (a.imageUrl && !b.imageUrl) 
                            return -1;
                        if (a.imageUrl && b.imageUrl) 
                            return a.id - b.id;
                        if (!a.imageUrl && b.imageUrl) 
                            return 1;
                    })
                    .map((product) => (<SearchResultsItemProduct
                        onClick={() => setSearchExpanded(false)}
                        key={product.id}
                        title={product.title}
                        descriptionHtml={product.descriptionHtml}
                        handle={product.handle}
                        query={searchQuery.q}
                        imageUrl={product.imageUrl}
                        variants={product.Variants ?? []}
                        sku={product.sku}
                        price={product.price} />))}
            </SearchResultsGroup>)}
            <Link
                onClick={() => setSearchExpanded(false)}
                to={genResultsUrl()}
                className={'search-results--show-all'}>Show all results for &ldquo;<strong>{searchQuery.q}</strong>&rdquo;.</Link>
        </div>)}
    </div>);
}

// SearchBox.propTypes = {
//     isMobile: bool,
//     isPage: bool,
//     productList: array,
//     setProductList: func,
//     searchLoaded: bool,
//     setSearchLoaded: func,
//     searchLoading: bool,
//     setSearchLoading: func,
//     searchQuery: object,
//     setSearchQuery: func,
//     setPagesData: func,
// };

export default forwardRef(SearchBox);